/* eslint-disable max-lines */
import { Input, Tab, Tabs, Text } from "@clipboard-health/ui-components";
import { getSafeAreaPaddings, getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Box, Stack, ThemeProvider } from "@mui/material";
import { APP_V2_APP_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { StickyFooter } from "@src/appV2/redesign/components/StickyFooter";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { z } from "zod";

import { PageWrapper } from "../../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../../components/StickyScrollPageHeader";
import { PageContent } from "../../../WorkerShifts/components/PageContent";
import { useHiringInstantPay } from "../../hooks/useHiringInstantPay";
import { PLACEMENT_PATHS } from "../../paths";
import { useConfetti } from "../HiringInstantPayConfettiProvider";
import { DateInput } from "./DateInput";
import { InstantPayVisualization } from "./InstantPayVisualization";
import { MoneyInput } from "./MoneyInput";
import { TimeInput } from "./TimeInput";

const MAX_SHIFTS = 5;

const shiftSchema = z
  .object({
    facilityName: z.string().min(1, "Facility name is required"),
    hourlyRate: z
      .number({ invalid_type_error: "Hourly rate must be a number" })
      .positive("Hourly rate must be greater than 0")
      .max(500, "Hourly rate seems too high"),
    shiftDate: z.date(),
    shiftStartTime: z.date(),
    shiftEndTime: z.date(),
  })
  .refine(
    (data) => {
      if (data.shiftEndTime && data.shiftStartTime) {
        return data.shiftEndTime > data.shiftStartTime;
      }

      return true;
    },
    {
      message: "Shift end must be after shift start",
      path: ["shiftEndTime"],
    }
  );
type ShiftForInstantPay = z.infer<typeof shiftSchema>;

const hiringInstantPayFormSchema = z.object({
  shifts: z.array(shiftSchema).min(1, "At least one shift is required"),
  payoutAmount: z.number().min(0, "Payout amount must be greater than 0"),
});

type HiringInstantPayFormValues = z.infer<typeof hiringInstantPayFormSchema>;

export function HiringInstantPayFormPage() {
  const history = useHistory();
  const { userId: workerId, tmz } = useDefinedWorker();
  const { showSuccessToast, showErrorToast } = useToast();
  const { triggerConfetti } = useConfetti();
  const [activeShiftIndex, setActiveShiftIndex] = useState(0);

  useLogEffect(
    APP_V2_APP_EVENTS.HIRING_INSTANT_PAY_FORM_VIEWED,
    { workerId, tmz },
    {
      enabled: true,
    }
  );
  const { enabled } = useHiringInstantPay();

  useEffect(() => {
    if (!enabled) {
      history.push(PLACEMENT_PATHS.ROOT);
    }
  }, [enabled, history]);

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<HiringInstantPayFormValues>({
    resolver: zodResolver(hiringInstantPayFormSchema),
    defaultValues: {
      shifts: [{ facilityName: "" }],
      payoutAmount: 0,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "shifts",
  });

  const handleAddShift = useCallback(() => {
    const newIndex = fields.length;
    append({ facilityName: "" } as ShiftForInstantPay); // when at a hackathon...
    setActiveShiftIndex(newIndex);
  }, [fields.length, append]);

  const handleDeleteShift = useCallback(
    (index: number) => {
      if (fields.length === 1) {
        showErrorToast("You must have at least one shift");
        return;
      }

      remove(index);

      if (activeShiftIndex >= fields.length - 1) {
        setActiveShiftIndex(Math.max(0, fields.length - 2));
      }
    },
    [fields.length, remove, activeShiftIndex, showErrorToast]
  );

  const formValues = watch();
  const watchedShifts = formValues.shifts;
  const recalculationKey = watchedShifts
    .map((shift) => {
      return isDefined(shift.shiftStartTime) &&
        isDefined(shift.shiftEndTime) &&
        isDefined(shift.hourlyRate)
        ? shift.shiftStartTime.toISOString() + shift.shiftEndTime.toISOString() + shift.hourlyRate
        : "";
    })
    .join(",");

  const totalPay = useMemo(() => {
    if (!watchedShifts || watchedShifts.length === 0 || recalculationKey === "") {
      return 0;
    }

    return watchedShifts.reduce((accumulator, shift) => {
      if (
        !isDefined(shift.shiftStartTime) ||
        !isDefined(shift.shiftEndTime) ||
        !isDefined(shift.hourlyRate) ||
        shift.hourlyRate <= 0
      ) {
        return accumulator;
      }

      const startTime = new Date(shift.shiftStartTime);
      const endTime = new Date(shift.shiftEndTime);
      const hoursWorked = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
      if (hoursWorked > 0) {
        return accumulator + hoursWorked * shift.hourlyRate;
      }

      return accumulator;
    }, 0);
  }, [watchedShifts, recalculationKey]);

  const onSubmit = useCallback(
    async (data: HiringInstantPayFormValues) => {
      const mappedData = data.shifts.map((shift) => {
        const startTime = new Date(shift.shiftDate);
        startTime.setHours(shift.shiftStartTime.getHours());
        startTime.setMinutes(shift.shiftStartTime.getMinutes());
        startTime.setSeconds(0);
        startTime.setMilliseconds(0);
        const endTime = new Date(shift.shiftDate);
        endTime.setHours(shift.shiftEndTime.getHours());
        endTime.setMinutes(shift.shiftEndTime.getMinutes());
        endTime.setSeconds(0);
        endTime.setMilliseconds(0);

        return {
          facilityName: shift.facilityName,
          shiftDate: shift.shiftDate.toISOString(),
          shiftStartTime: startTime.toISOString(),
          shiftEndTime: endTime.toISOString(),
          hourlyRate: shift.hourlyRate,
        };
      });

      const localEventContext = {
        workerId,
        tmz,
        data: mappedData,
        numberOfShifts: data.shifts.length,
        totalPay: totalPay.toFixed(2),
        payoutAmount: data.payoutAmount.toFixed(2),
        eligibleAmount: (totalPay * 0.6).toFixed(2),
      };

      try {
        logEvent(APP_V2_APP_EVENTS.HIRING_INSTANT_PAY_FORM_SUBMITTED, localEventContext);

        triggerConfetti();
        showSuccessToast(`Your request for $${data.payoutAmount.toFixed(2)} has been submitted!`);
        history.push(PLACEMENT_PATHS.ROOT);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to submit form. Please try again.";

        logEvent(APP_V2_APP_EVENTS.HIRING_INSTANT_PAY_FORM_SUBMISSION_FAILED, {
          ...localEventContext,
          error: errorMessage,
        });

        showErrorToast(errorMessage);
      }
    },
    [showSuccessToast, showErrorToast, triggerConfetti, history, totalPay, workerId, tmz]
  );

  const currentShiftErrors = errors.shifts?.[activeShiftIndex];

  if (!enabled) {
    return null;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper
        sx={(theme) => ({
          ...getSafeAreaPaddings({
            bottom: true,
            fallbackBottom: theme.spacing(3),
          }),
          backgroundColor: theme.palette.background.tertiary,
          paddingX: 0,
        })}
      >
        <StickyScrollPageHeader title="Submit shift verification" headerVariant="tertiary" />

        <PageContent sx={{ padding: 0, height: "100%" }}>
          <Box
            component="form"
            id="hiring-instant-pay-form"
            sx={{ flex: 1 }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Stack spacing={6} sx={{ paddingX: 6, paddingY: 6 }}>
              <Box sx={{ display: "flex", justifyContent: "end" }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={isSubmitting || fields.length >= MAX_SHIFTS}
                  startIconType="plus"
                  onClick={handleAddShift}
                >
                  Add another shift
                </Button>
              </Box>

              {fields.length > 1 && (
                <Tabs
                  variant="pill"
                  value={activeShiftIndex}
                  sx={{ gap: 2 }}
                  onChange={(_, newValue) => {
                    setActiveShiftIndex(newValue as number);
                  }}
                >
                  {fields.map((field, index) => (
                    <Tab
                      key={field.id}
                      variant="pill"
                      label={`Shift #${index + 1}`}
                      value={index}
                    />
                  ))}
                </Tabs>
              )}

              <Stack key={fields[activeShiftIndex]?.id} spacing={6}>
                <Stack spacing={2}>
                  <Text semibold variant="body1">
                    Facility Name
                  </Text>
                  <Controller
                    name={`shifts.${activeShiftIndex}.facilityName`}
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        fullWidth
                        placeholder="Enter facility name"
                        error={isDefined(currentShiftErrors?.facilityName)}
                        disabled={isSubmitting}
                      />
                    )}
                  />
                </Stack>

                <Controller
                  name={`shifts.${activeShiftIndex}.shiftDate`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <DateInput
                      label="Date"
                      value={value}
                      disabled={isSubmitting}
                      error={isDefined(currentShiftErrors?.shiftDate)}
                      onChange={onChange}
                    />
                  )}
                />

                <Controller
                  name={`shifts.${activeShiftIndex}.shiftStartTime`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TimeInput
                      label="Start Time"
                      value={value}
                      disabled={isSubmitting}
                      error={isDefined(currentShiftErrors?.shiftStartTime)}
                      onChange={onChange}
                    />
                  )}
                />

                <Controller
                  name={`shifts.${activeShiftIndex}.shiftEndTime`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TimeInput
                      label="End Time"
                      value={value}
                      disabled={isSubmitting}
                      error={isDefined(currentShiftErrors?.shiftEndTime)}
                      onChange={onChange}
                    />
                  )}
                />

                <Controller
                  name={`shifts.${activeShiftIndex}.hourlyRate`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <MoneyInput
                      label="Wage $/hour"
                      value={value}
                      disabled={isSubmitting}
                      error={isDefined(currentShiftErrors?.hourlyRate)}
                      placeholder="Enter hourly rate"
                      onChange={onChange}
                    />
                  )}
                />

                {isDefined(currentShiftErrors?.shiftEndTime) &&
                  currentShiftErrors.shiftEndTime.type === "custom" && (
                    <Alert severity="error">{currentShiftErrors.shiftEndTime.message}</Alert>
                  )}

                {fields.length > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "end" }}>
                    <Button
                      size="small"
                      variant="destructive"
                      disabled={isSubmitting}
                      startIconType="remove"
                      sx={{ maxWidth: "fit-content" }}
                      onClick={() => {
                        handleDeleteShift(activeShiftIndex);
                      }}
                    >
                      Delete shift
                    </Button>
                  </Box>
                )}
              </Stack>

              <Controller
                name="payoutAmount"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InstantPayVisualization
                    totalPay={totalPay}
                    defaultValue={value}
                    onChange={onChange}
                  />
                )}
              />
            </Stack>
          </Box>

          <StickyFooter>
            <LoadingButton
              fullWidth
              form="hiring-instant-pay-form"
              type="submit"
              variant="contained"
              size="large"
              disabled={isSubmitting || totalPay <= 0 || Object.keys(errors).length > 0}
              isLoading={isSubmitting}
            >
              Get Paid ${formValues.payoutAmount.toFixed(2)}
            </LoadingButton>
          </StickyFooter>
        </PageContent>
      </PageWrapper>
    </ThemeProvider>
  );
}
/* eslint-enable max-lines */
